import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import {
  BorderlessFilterAccordion,
  FilterBadge,
  FilterClearIcon,
  SelectFormControlLabel,
  SelectLabelText,
  SelectLabelWrapper,
  SelectRadio,
} from '@modules/search/index.styles'
import {
  MetaFilterSelectedData,
  MetaFilterSelectedDate,
  MetaFilterTypes,
  NumbersRange,
  SearchFilterByRecent,
} from '@modules/search/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AccordionDetails, Box, RadioGroup, Typography } from '@mui/material'

import DateRangeSelect from '../ResultsSection/components/DateRangeSelect'
import { RECENT_LABELS } from '../ResultsSection/constants'
import { DateFilterOptionWrapper, FilterSectionAccordionSummary, FilterTitleBox, OuterFilterBox } from './index.styles'

export interface SearchByDateProps {
  handleSelectMetaFilter: (filterData?: MetaFilterSelectedData) => void
  selectedFilter: MetaFilterSelectedData
  eventsDatesRange: NumbersRange
  isFilterExpanded: boolean
  onExpand: (isExpanded: boolean) => void
  prefix: string
}

const SearchByDate = ({
  handleSelectMetaFilter,
  selectedFilter,
  isFilterExpanded,
  onExpand,
  prefix,
  eventsDatesRange,
}: SearchByDateProps) => {
  const { t } = useTranslation()

  const [selectedFrom, setSelectedFrom] = useState(eventsDatesRange.min)
  const [selectedTo, setSelectedTo] = useState(eventsDatesRange.max)

  const [selectedRecentValue, setSelectedRecentValue] = useState(
    selectedFilter?.type === MetaFilterTypes.BY_RECENT ? selectedFilter?.value : null,
  )

  const handleSelectedRangeUpdate = (from: number, to: number) => {
    handleSelectMetaFilter({ ...selectedFilter, type: MetaFilterTypes.BY_DATE_RANGE, value: { from, to } })
    setSelectedFrom(from)
    setSelectedTo(to)
  }

  useEffect(() => {
    const { from: incomingFrom, to: incomingTo } = selectedFilter?.value as MetaFilterSelectedDate

    if (selectedFilter?.type === MetaFilterTypes.BY_DATE_RANGE) {
      const hasChanged = incomingTo !== selectedTo || incomingFrom !== selectedFrom

      if (!hasChanged) {
        return
      }

      setSelectedFrom(incomingFrom)
      setSelectedTo(incomingTo)
    }

    if (selectedFilter.type === MetaFilterTypes.BY_RECENT) {
      const hasChanged = selectedRecentValue !== selectedFilter.value

      if (!hasChanged) {
        return
      }

      setSelectedRecentValue(selectedFilter.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter.value, selectedFilter.type])

  const FILTER_VALUES_MAP = {
    [MetaFilterTypes.BY_DATE_RANGE]: { from: selectedFrom, to: selectedTo },
    [MetaFilterTypes.BY_EVENT]: '',
    [MetaFilterTypes.BY_RECENT]: selectedRecentValue || '',
    [MetaFilterTypes.BY_NEW_ITEMS]: 'NEW_ITEMS_PLACEHOLDER',
    [MetaFilterTypes.DEFAULT]: '',
  }

  const checkByType = (type: MetaFilterTypes) => {
    return selectedFilter?.type === type
  }

  const handleRadioChange = useCallback(
    (type: MetaFilterTypes, value?: SearchFilterByRecent) => {
      if (type in MetaFilterTypes) {
        handleSelectMetaFilter({ ...selectedFilter, type, value: value || FILTER_VALUES_MAP[type] })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectMetaFilter, selectedFilter],
  )

  const recentFilterOptions = useMemo(
    () =>
      RECENT_LABELS.map(button => (
        <SelectFormControlLabel
          key={button.value}
          value={button.value}
          control={
            <Box
              onClick={e => {
                e.stopPropagation()
                setSelectedRecentValue(button.value)
                handleRadioChange(MetaFilterTypes.BY_RECENT, button.value)
              }}
            >
              <SelectRadio
                checked={selectedFilter.type === MetaFilterTypes.BY_RECENT && selectedRecentValue === button.value}
                disableRipple
                inputProps={{ id: button.id }}
              />
            </Box>
          }
          label={
            <SelectLabelWrapper>
              <SelectLabelText variant="body2" data-testid="single_select_label">
                {t(button.label)}
              </SelectLabelText>
            </SelectLabelWrapper>
          }
        />
      )),
    [selectedFilter.type, selectedRecentValue, t, handleRadioChange],
  )

  const isFilteredBy = selectedFilter?.type && selectedFilter.type !== MetaFilterTypes.DEFAULT
  const selectedType = useMemo(() => {
    if (!selectedFilter?.type) return MetaFilterTypes.DEFAULT

    return selectedFilter.type
  }, [selectedFilter.type])

  return (
    <OuterFilterBox $active={isFilteredBy}>
      <BorderlessFilterAccordion disableGutters onChange={() => onExpand(!isFilterExpanded)} expanded={!!isFilterExpanded}>
        <FilterSectionAccordionSummary
          sx={{ flexDirection: 'row' }}
          expandIcon={<ExpandMoreIcon />}
          data-testid="filter_accordion_summary"
          id="search-filter-profile_section"
        >
          <FilterTitleBox>
            <Typography variant="h2">
              {t(translationKeys.SEARCH_BY_PROFILE_UPDATED)}
              {isFilteredBy && (
                <FilterBadge variant="dot" color="primary" data-testid={`${prefix?.toLowerCase()}_filter_badge`} />
              )}
            </Typography>
            {isFilteredBy && (
              <FilterClearIcon
                id="date_filter-reset"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  handleSelectMetaFilter({ ...selectedFilter, type: MetaFilterTypes.DEFAULT, value: '' })
                }}
                color="primary"
                data-testid="filter_clear_section"
              />
            )}
          </FilterTitleBox>
        </FilterSectionAccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <RadioGroup value={selectedType} onChange={event => handleRadioChange(event.target.value as MetaFilterTypes)}>
            <SelectFormControlLabel
              key={MetaFilterTypes.DEFAULT}
              value={MetaFilterTypes.DEFAULT}
              control={<SelectRadio disableRipple inputProps={{ id: 'date-all' }} />}
              label={
                <SelectLabelWrapper>
                  <Typography variant="body1">{t(translationKeys.SEARCH_FILTER_BY_DATE_LABEL_EMPTY)}</Typography>
                </SelectLabelWrapper>
              }
            />
            {recentFilterOptions}
            <SelectFormControlLabel
              key={MetaFilterTypes.BY_DATE_RANGE}
              value={MetaFilterTypes.BY_DATE_RANGE}
              control={<SelectRadio disableRipple inputProps={{ id: 'date-date_range' }} />}
              label={
                <DateFilterOptionWrapper>
                  <Typography variant="body1">{t(translationKeys.SEARCH_FILTER_BY_DATE_RANGE)}</Typography>
                  <DateRangeSelect
                    identifier="date-date_range"
                    selectedDateFrom={selectedFrom}
                    selectedDateTo={selectedTo}
                    eventsDatesRange={eventsDatesRange}
                    setDateRangeSelectEnd={newDateEnd => handleSelectedRangeUpdate(selectedFrom, newDateEnd)}
                    setDateRangeSelectStart={newDateStart => handleSelectedRangeUpdate(newDateStart, selectedTo)}
                    isSelected={checkByType(MetaFilterTypes.BY_DATE_RANGE)}
                  />
                </DateFilterOptionWrapper>
              }
            />
          </RadioGroup>
        </AccordionDetails>
      </BorderlessFilterAccordion>
    </OuterFilterBox>
  )
}

export default memo(SearchByDate)
